import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { NIKO_API_URL } from '../tokens/api.tokens';
import { User, MyTenant } from '../models/user.model';
import { ImpersonationMeta } from '../models/impersonation.model';
import { Catalog } from '../models/catalog.model';
import { UserPermissionEntry } from '../models/permission.model';
import { PaginatedResult } from '../models/paginated-result.model';

export interface MeResponse {
  user: User;
  permissions: string[];
  impersonation: ImpersonationMeta | null;
}

@Injectable({ providedIn: 'root' })
export class UserService {
  private readonly http = inject(HttpClient);
  private readonly base = inject(NIKO_API_URL);

  /** `impersonation` is `null` outside an impersonation session (BR-AUTH-03). */
  getMe(): Observable<MeResponse> {
    return this.http.get<MeResponse>(`${this.base}/users/me`);
  }

  /**
   * Pre-selection call — lists every tenant the current cognitoSub belongs
   * to. Used by a normal user with N>1 memberships to feed the tenant picker
   * (spec §4, `GET /users/me/tenants`).
   */
  getMyTenants(): Observable<MyTenant[]> {
    return this.http.get<MyTenant[]>(`${this.base}/users/me/tenants`);
  }

  getUsers(page = 1, limit = 20, search?: string): Observable<PaginatedResult<User>> {
    const params: Record<string, string> = { page: page.toString(), limit: limit.toString() };
    const term = search?.trim();
    if (term) params['search'] = term;
    return this.http.get<PaginatedResult<User>>(`${this.base}/users`, { params });
  }

  getUser(id: string): Observable<User> {
    return this.http.get<User>(`${this.base}/users/${id}`);
  }

  createUser(payload: Partial<User>): Observable<User> {
    return this.http.post<User>(`${this.base}/users`, payload);
  }

  updateUser(id: string, payload: Partial<User>): Observable<User> {
    return this.http.patch<User>(`${this.base}/users/${id}`, payload);
  }

  /**
   * Soft delete — flips `active` to false and disables the Cognito login.
   * The row is never removed, so the API returns the updated user (BR-USER-09).
   */
  deleteUser(id: string): Observable<User> {
    return this.http.delete<User>(`${this.base}/users/${id}`);
  }

  /** Inverse of `deleteUser`. Requires `users:delete`, not `users:edit`. */
  reactivateUser(id: string): Observable<User> {
    return this.http.patch<User>(`${this.base}/users/${id}/reactivate`, {});
  }

  resendInvitation(id: string): Observable<User> {
    return this.http.patch<User>(`${this.base}/users/${id}/resend-invitation`, {});
  }

  /** Email-less password reset (admin portal, SuperAdmin). Returns a one-time temporary password. */
  resetPassword(id: string): Observable<{ user: User; temporaryPassword: string }> {
    return this.http.post<{ user: User; temporaryPassword: string }>(
      `${this.base}/users/${id}/reset-password`,
      {},
    );
  }

  getUserPermissions(userId: string): Observable<UserPermissionEntry[]> {
    return this.http.get<UserPermissionEntry[]>(`${this.base}/users/${userId}/permissions`);
  }

  setUserPermission(userId: string, permissionId: string, granted: boolean): Observable<void> {
    return this.http.post<void>(`${this.base}/users/${userId}/permissions`, {
      permissionId,
      granted,
    });
  }

  assignUserPermissions(userId: string, permissionIds: string[]): Observable<void> {
    return this.http.patch<void>(`${this.base}/users/${userId}/permissions`, { permissionIds });
  }

  getUserGroups(userId: string): Observable<Catalog[]> {
    return this.http.get<Catalog[]>(`${this.base}/users/${userId}/groups`);
  }

  updateUserGroups(userId: string, groupIds: string[]): Observable<void> {
    return this.http.put<void>(`${this.base}/users/${userId}/groups`, { groupIds });
  }
}
